import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type {
  InsightAggregateMetrics,
  InsightCategory,
  InsightLens,
  InsightRaw,
  InsightStatus,
} from '../types';

export const INSIGHTS_KEY = ['insights'] as const;

/**
 * Lente default = `supervisor` (Tela 10). A Tela 12 do Gestor é o follow-up (D13): passa
 * `lens: 'gestor'`. A lente entra no queryKey para isolar o cache das duas telas.
 */
const DEFAULT_LENS: InsightLens = 'supervisor';

interface InsightListResult {
  rows: InsightRaw[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
}

/** Fila de pendentes (cursor). Filtros `category`/`period` server-side (D12). */
export function useInsights(
  status: InsightStatus,
  opts: { category?: InsightCategory; period?: string; lens?: InsightLens } = {},
): InsightListResult {
  const lens = opts.lens ?? DEFAULT_LENS;
  const query = useInfiniteQuery({
    queryKey: [...INSIGHTS_KEY, 'list', lens, status, opts.category ?? null, opts.period ?? null],
    queryFn: ({ pageParam }) =>
      api.listInsights({
        status,
        lens,
        ...(opts.category !== undefined ? { category: opts.category } : {}),
        ...(opts.period !== undefined ? { period: opts.period } : {}),
        ...(pageParam != null ? { cursor: pageParam } : {}),
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 15 * 1000,
  });
  return {
    rows: (query.data?.pages ?? []).flatMap((p) => p.items),
    fetchNextPage: () => void query.fetchNextPage(),
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** Histórico (default status IN [accepted, archived] no backend) — "aceitos hoje" (D8). */
export function useInsightsHistory(
  opts: { category?: InsightCategory; period?: string; lens?: InsightLens } = {},
): InsightListResult {
  const lens = opts.lens ?? DEFAULT_LENS;
  const query = useInfiniteQuery({
    queryKey: [...INSIGHTS_KEY, 'history', lens, opts.category ?? null, opts.period ?? null],
    queryFn: ({ pageParam }) =>
      api.listInsightsHistory({
        lens,
        ...(opts.category !== undefined ? { category: opts.category } : {}),
        ...(pageParam != null ? { cursor: pageParam } : {}),
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 15 * 1000,
  });
  return {
    rows: (query.data?.pages ?? []).flatMap((p) => p.items),
    fetchNextPage: () => void query.fetchNextPage(),
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** Métricas agregadas (subtítulo "N aceitos hoje" — D8). */
export function useInsightMetrics(
  period?: string,
  lens: InsightLens = DEFAULT_LENS,
): {
  metrics: InsightAggregateMetrics | undefined;
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: [...INSIGHTS_KEY, 'metrics', lens, period ?? null],
    queryFn: () =>
      api.insightAggregateMetrics({ lens, ...(period !== undefined ? { period } : {}) }),
    staleTime: 30 * 1000,
  });
  return { metrics: q.data, isLoading: q.isLoading };
}
